import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Plus,
  Edit3,
  Trash2,
  Search,
  UserRoundCog,
  Shield,
  User,
  AlertCircle,
  Crown,
  Loader2,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import UserFormModal from "./UserFormModal";
import ProfilesManagement from "./ProfilesManagement";
import MenusManagement from "@/components/menus/MenusManagement";
import PermissionsMatrix from "@/components/menus/PermissionsMatrix";
import userService from "@/services/userService";
import profileService from "@/services/profileService";
import menuService from "@/services/menuService";
import companyService from "@/services/companyService";
import { showError, showInfo } from "@/store/errorStore";
import { useAuth } from "@/hooks/useAuth";

/**
 * Erros HTTP já são exibidos pelo modal global (axiosInterceptor);
 * só mostramos aqui erros sem resposta do servidor (ex.: falha local).
 */
const showLocalError = (error, fallback) => {
  if (!error?.response && !error?.request) {
    showError(error?.message || fallback, "Erro");
  }
};

const ROLE_CONFIG = {
  ROLE_OWNER: {
    label: "Proprietário",
    className: "bg-purple-100 text-purple-800 border-purple-200",
    icon: Crown,
  },
  ROLE_ADMIN: {
    label: "Administrador",
    className: "bg-red-100 text-red-800 border-red-200",
    icon: Shield,
  },
  ROLE_USER: {
    label: "Usuário",
    className: "bg-gray-100 text-gray-800 border-gray-200",
    icon: User,
  },
};

const getRoleConfig = (role) => {
  const name = userService.normalizeRole(role);
  return (
    ROLE_CONFIG[name] || {
      label: profileService.getProfileLabel(name),
      className: "bg-blue-100 text-blue-800 border-blue-200",
      icon: UserRoundCog,
    }
  );
};

const RoleBadge = ({ role }) => {
  const config = getRoleConfig(role);
  const IconComponent = config.icon;
  return (
    <Badge variant="outline" className={config.className}>
      <IconComponent className="w-3 h-3 mr-1" />
      {config.label}
    </Badge>
  );
};

/**
 * Componente de gerenciamento de usuários
 * CRUD completo com listagem, criação, edição e exclusão
 */
export default function UsersManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [savingUser, setSavingUser] = useState(false);
  const [loadingUserId, setLoadingUserId] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState("users");
  const [profiles, setProfiles] = useState([]);
  const [menus, setMenus] = useState([]);
  const [loadingProfiles, setLoadingProfiles] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [companyFilter, setCompanyFilter] = useState("all");

  const { user: currentUser } = useAuth();
  const isOwner = userService.isOwner(currentUser);
  // Cadastrar/editar/remover usuários: ROLE_ADMIN (na própria empresa) e
  // ROLE_OWNER (em qualquer empresa). Demais perfis: só consulta.
  const canManageUsers = userService.canManageUsers(currentUser);
  // Escolher a empresa do usuário e ver todas as empresas: só ROLE_OWNER
  const canChooseCompany = userService.canChooseCompany(currentUser);
  // Não-owner: a API já devolve só os usuários da própria empresa
  const callerCompanyCode = userService.getCallerCompanyCode(currentUser);
  const withoutCompany = callerCompanyCode === "";
  const callerCompanyName =
    companies.find((c) => c.code === callerCompanyCode)?.name ||
    callerCompanyCode;
  const currentEmail = (currentUser?.email || "").toLowerCase();
  const isCurrentUser = (user) =>
    (user?.email || "").toLowerCase() === currentEmail;

  useEffect(() => {
    fetchUsers();
    // Nomes das empresas: owner lista todas; os demais recebem só a própria
    fetchCompanies();
    // Perfis e menus (/profiles, /menus/all) exigem ROLE_ADMIN/ROLE_OWNER
    if (canManageUsers) {
      fetchProfiles();
    } else {
      setLoadingProfiles(false);
    }
  }, [canManageUsers]);

  /**
   * Buscar empresas para o vínculo usuário x empresa (company_code)
   */
  const fetchCompanies = async () => {
    try {
      const data = await companyService.getAllCompanies();
      setCompanies(Array.isArray(data) ? data : []);
    } catch (error) {
      showLocalError(error, "Erro ao carregar empresas");
      setCompanies([]);
    }
  };

  /**
   * Buscar perfis (bf_role) e menus disponíveis
   */
  const fetchProfiles = async () => {
    setLoadingProfiles(true);
    try {
      const [profilesData, menusData] = await Promise.all([
        profileService.getAllProfiles(),
        menuService.getAllMenus(),
      ]);
      setProfiles(Array.isArray(profilesData) ? profilesData : []);
      setMenus(Array.isArray(menusData) ? menusData : []);
    } catch (error) {
      showLocalError(error, "Erro ao carregar perfis");
    } finally {
      setLoadingProfiles(false);
    }
  };

  /**
   * Recarregar perfis e usuários após alterações em perfis
   */
  const reloadProfilesAndUsers = async () => {
    await Promise.all([fetchProfiles(), fetchUsers()]);
  };

  /**
   * Buscar todos os usuários
   */
  const fetchUsers = async () => {
    // Perfil sem empresa e sem ser Administrador não enxerga usuários
    if (withoutCompany) {
      setUsers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await userService.getAllUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      showLocalError(error, "Erro ao carregar usuários");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Abrir modal para criar novo usuário
   */
  const handleCreateUser = () => {
    if (!canManageUsers) return;
    setEditingUser(null);
    setShowModal(true);
  };

  /**
   * Abrir modal para editar usuário (busca dados atualizados por ID)
   */
  const handleEditUser = async (user) => {
    if (!canManageUsers) return;
    setLoadingUserId(user.id);
    try {
      const freshUser = await userService.getUserById(user.id);
      setEditingUser(freshUser || user);
      setShowModal(true);
    } catch (error) {
      showLocalError(error, "Erro ao carregar usuário");
      if (error.response?.status === 404) {
        await fetchUsers();
      }
    } finally {
      setLoadingUserId(null);
    }
  };

  /**
   * Salvar usuário (criar ou atualizar)
   */
  const handleSaveUser = async (userData) => {
    setSavingUser(true);
    try {
      if (editingUser) {
        // Atualizar usuário existente
        await userService.updateUser(editingUser.id, userData);
        showInfo(
          `Usuário "${userData.email}" atualizado com sucesso`,
          "Usuário Atualizado",
        );
      } else {
        // Criar novo usuário
        await userService.createUser(userData);
        showInfo(
          `Usuário "${userData.email}" criado com sucesso`,
          "Usuário Criado",
        );
      }

      setShowModal(false);
      setEditingUser(null);
      await fetchUsers();
    } catch (error) {
      showLocalError(error, "Erro ao salvar usuário");
    } finally {
      setSavingUser(false);
    }
  };

  /**
   * Confirmar exclusão de usuário
   */
  const handleDeleteClick = (user) => {
    if (!canManageUsers) return;
    setDeletingUser(user);
    setShowDeleteDialog(true);
  };

  /**
   * Executar exclusão de usuário
   */
  const handleConfirmDelete = async () => {
    if (!deletingUser) return;

    setDeleting(true);
    try {
      await userService.deleteUser(deletingUser.id);
      showInfo(
        `Usuário "${deletingUser.email}" removido com sucesso`,
        "Usuário Removido",
      );
      await fetchUsers();
    } catch (error) {
      showLocalError(error, "Erro ao remover usuário");
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
      setDeletingUser(null);
    }
  };

  /**
   * Filtrar usuários por busca
   */
  const filteredUsers = users.filter((user) => {
    if (companyFilter === "none" && user.company_code) return false;
    if (
      companyFilter !== "all" &&
      companyFilter !== "none" &&
      user.company_code !== companyFilter
    ) {
      return false;
    }

    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;
    const company = (user.company_name || "").toLowerCase();
    const email = (user.email || "").toLowerCase();
    const username = (user.username || "").toLowerCase();
    const roles = (user.roles || [])
      .map((role) => `${role} ${getRoleConfig(role).label}`)
      .join(" ")
      .toLowerCase();
    return (
      email.includes(query) ||
      username.includes(query) ||
      roles.includes(query) ||
      company.includes(query)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Gerenciamento de Usuários
          </h1>
          <p className="text-gray-500 mt-1">
            {filteredUsers.length}{" "}
            {filteredUsers.length === 1 ? "usuário" : "usuários"} cadastrados
          </p>
        </div>

        {activeTab === "users" && canManageUsers && (
          <Button
            onClick={handleCreateUser}
            style={{ background: "#1b5e20" }}
            onMouseEnter={(e) => (e.target.style.background = "#144919")}
            onMouseLeave={(e) => (e.target.style.background = "#1b5e20")}
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Usuário
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {canManageUsers && (
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="users">Usuários</TabsTrigger>
            <TabsTrigger value="profiles">Perfis de Acesso</TabsTrigger>
            <TabsTrigger value="permissions">Permissões</TabsTrigger>
            <TabsTrigger value="menus">Menus</TabsTrigger>
          </TabsList>
        )}

        <TabsContent value="users" className="space-y-6">
          {/* Search */}
          <Card>
            <CardHeader>
              <CardTitle>Buscar Usuários</CardTitle>
              <CardDescription>
                {withoutCompany
                  ? "Seu usuário não está vinculado a uma empresa"
                  : callerCompanyCode
                    ? `Exibindo apenas usuários da empresa ${callerCompanyName}`
                    : "Pesquise por nome, email, função ou empresa do usuário"}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3 md:flex-row">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  placeholder="Digite o nome, email, função ou empresa..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              {!callerCompanyCode && (
                <Select value={companyFilter} onValueChange={setCompanyFilter}>
                  <SelectTrigger
                    className="md:w-64"
                    aria-label="Filtrar por empresa"
                  >
                    <SelectValue placeholder="Empresa" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as empresas</SelectItem>
                    <SelectItem value="none">Sem empresa vinculada</SelectItem>
                    {companies
                      .filter((c) => c?.code)
                      .map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.name || c.code}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              )}
            </CardContent>
          </Card>

          {/* Users Table */}
          <Card>
            <CardHeader>
              <CardTitle>Lista de Usuários</CardTitle>
              <CardDescription>
                {canChooseCompany
                  ? "Gerencie os usuários de todas as empresas"
                  : canManageUsers
                    ? "Gerencie os usuários da sua empresa"
                    : "Usuários da sua empresa (somente consulta)"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
                    <p className="text-gray-600">Carregando usuários...</p>
                  </div>
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <AlertCircle className="w-12 h-12 text-gray-400 mb-3" />
                  <p className="text-gray-600 font-medium">
                    {searchQuery || companyFilter !== "all"
                      ? "Nenhum usuário encontrado"
                      : "Nenhum usuário cadastrado"}
                  </p>
                  <p className="text-gray-500 text-sm mt-1">
                    {searchQuery || companyFilter !== "all"
                      ? "Tente outro termo de busca ou filtro"
                      : withoutCompany
                        ? "Peça a um Administrador para vincular seu usuário a uma empresa"
                        : canManageUsers
                          ? "Clique em 'Novo Usuário' para começar"
                          : "Nenhum usuário vinculado à sua empresa"}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ID</TableHead>
                        <TableHead>Nome</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Empresa</TableHead>
                        <TableHead>Funções</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Criado em</TableHead>
                        {canManageUsers && (
                          <TableHead className="text-right">Ações</TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell className="font-mono text-sm text-gray-500">
                            #{user.id}
                          </TableCell>
                          <TableCell className="font-medium">
                            {user.username || (
                              <span className="text-gray-400">-</span>
                            )}
                            {isCurrentUser(user) && (
                              <Badge variant="secondary" className="ml-2">
                                Você
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell className="text-sm">
                            {user.company_code ? (
                              user.company_name || (
                                <span className="font-mono text-xs text-gray-500">
                                  {user.company_code}
                                </span>
                              )
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {(user.roles || []).length > 0 ? (
                                user.roles.map((role) => (
                                  <RoleBadge key={role} role={role} />
                                ))
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {user.enabled === false ? (
                              <Badge
                                variant="outline"
                                className="bg-gray-100 text-gray-600 border-gray-200"
                              >
                                Inativo
                              </Badge>
                            ) : (
                              <Badge
                                variant="outline"
                                className="bg-green-100 text-green-800 border-green-200"
                              >
                                Ativo
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-gray-500">
                            {userService.formatDate(user.created_at)}
                          </TableCell>
                          {canManageUsers && (
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEditUser(user)}
                                  disabled={loadingUserId === user.id}
                                >
                                  {loadingUserId === user.id ? (
                                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                  ) : (
                                    <Edit3 className="w-4 h-4 mr-1" />
                                  )}
                                  Editar
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleDeleteClick(user)}
                                  disabled={isCurrentUser(user)}
                                  title={
                                    isCurrentUser(user)
                                      ? "Não é permitido remover o próprio usuário"
                                      : undefined
                                  }
                                >
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  Excluir
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="profiles">
          <ProfilesManagement
            profiles={profiles}
            menus={menus}
            users={users}
            loading={loadingProfiles}
            isOwner={isOwner}
            onReload={reloadProfilesAndUsers}
          />
        </TabsContent>

        <TabsContent value="permissions">
          <PermissionsMatrix
            profiles={profiles}
            menus={menus}
            currentUser={currentUser}
            isOwner={isOwner}
            loading={loadingProfiles}
            onReload={fetchProfiles}
          />
        </TabsContent>

        <TabsContent value="menus">
          <MenusManagement
            menus={menus}
            profiles={profiles}
            loading={loadingProfiles}
            onReload={fetchProfiles}
            isOwner={isOwner}
          />
        </TabsContent>
      </Tabs>

      {/* User Form Modal */}
      <UserFormModal
        open={showModal}
        onOpenChange={setShowModal}
        user={editingUser}
        onSave={handleSaveUser}
        loading={savingUser}
        isOwner={isOwner}
        canChooseCompany={canChooseCompany}
        profiles={profiles}
        menus={menus}
        loadingProfiles={loadingProfiles}
        companies={companies}
        defaultCompanyCode={currentUser?.company_code || null}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o usuário{" "}
              <strong>{deletingUser?.email}</strong>? Esta ação não pode ser
              desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setDeletingUser(null)}
              disabled={deleting}
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleting}
            >
              {deleting ? "Excluindo..." : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
